import React, { useState, useEffect } from 'react';
import { superAdminApi } from '../../services/superAdminApi';
import DomainStats from '../../components/super-admin/DomainStats';
import DomainConfigModal from '../../components/super-admin/DomainConfigModal';
import DnsInstructions from '../../components/super-admin/DnsInstructions';
import DomainStatus from '../../components/super-admin/DomainStatus';

const DomainManagement = () => {
  const [stats, setStats] = useState(null);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDnsModal, setShowDnsModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    domain_type: '',
    search: '',
    ssl_expiring: false
  });

  useEffect(() => {
    fetchStats();
    fetchDomains();
  }, [filters]);

  const fetchStats = async () => {
    try {
      const response = await superAdminApi.getDomainStats();
      console.log('Stats API response:', response.data);
      
      // Handle both response structures
      if (response.data.data) {
        setStats(response.data.data);
      } else {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch domain stats:', error);
      setStats(null);
    }
  };

  const fetchDomains = async (page = 1) => {
    try {
      setLoading(true);
      const params = { ...filters, page, per_page: 15 };
      const response = await superAdminApi.getDomains(params);
      
      console.log('Domain API response:', response.data);
      
      // Handle both response structures
      if (response.data.data) {
        // New API structure: { success: true, data: { domains: [], pagination: {} } }
        setDomains(response.data.data.domains || []);
        setPagination(response.data.data.pagination || null);
      } else {
        // Old API structure: { domains: [], pagination: {} }
        setDomains(response.data.domains || []);
        setPagination(response.data.pagination || null);
      }
    } catch (error) {
      console.error('Failed to fetch domains:', error);
      setDomains([]); // Ensure domains is always an array
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDomainCreated = () => {
    fetchStats();
    fetchDomains();
    setShowConfigModal(false);
  };

  const handleVerifyDns = async (domainId) => {
    try {
      await superAdminApi.verifyDomainDns(domainId);
      fetchDomains();
    } catch (error) {
      console.error('Failed to verify DNS:', error);
    }
  };

  const handleEnableSsl = async (domainId) => {
    try {
      await superAdminApi.enableDomainSsl(domainId);
      fetchDomains();
    } catch (error) {
      console.error('Failed to enable SSL:', error);
    }
  };

  const handleShowDnsInstructions = (domain) => {
    setSelectedDomain(domain);
    setShowDnsModal(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleBulkAction = async (action, domainIds) => {
    try {
      await superAdminApi.bulkDomainAction(action, domainIds);
      fetchDomains();
    } catch (error) {
      console.error('Bulk action failed:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { class: 'status-active', icon: 'check-circle', text: 'Active' },
      pending: { class: 'status-pending', icon: 'clock', text: 'Pending' },
      failed: { class: 'status-failed', icon: 'times-circle', text: 'Failed' },
      suspended: { class: 'status-suspended', icon: 'pause-circle', text: 'Suspended' }
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`status-badge ${badge.class}`}>
        <i className={`fas fa-${badge.icon}`}></i>
        {badge.text}
      </span>
    );
  };

  return (
    <div className="domain-management">
      <div className="page-header">
        <div className="header-content">
          <h1>Domain Management</h1>
          <p>Manage subdomains and custom domains for tenant instances</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => setShowConfigModal(true)}
            className="btn-primary"
          >
            <i className="fas fa-plus"></i>
            Add Domain
          </button>
        </div>
      </div>

      <DomainStats stats={stats} loading={!stats} />

      <div className="domain-table-container">
        <div className="table-filters">
          <div className="filter-row">
            <div className="filter-group">
              <label>Status</label>
              <select 
                className="filter-select"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Type</label>
              <select 
                className="filter-select"
                value={filters.domain_type}
                onChange={(e) => handleFilterChange('domain_type', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="subdomain">Subdomain</option>
                <option value="custom_domain">Custom Domain</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Search</label>
              <input
                type="text"
                className="filter-input"
                placeholder="Search domains..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>
                <input
                  type="checkbox"
                  checked={filters.ssl_expiring}
                  onChange={(e) => handleFilterChange('ssl_expiring', e.target.checked)}
                />
                SSL Expiring Soon
              </label>
            </div>
          </div>
        </div>

        <div className="table-container">
          {loading ? (
            <div className="table-loading">
              <i className="fas fa-spinner fa-spin"></i>
              Loading domains...
            </div>
          ) : (
            <table className="domains-table">
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" />
                  </th>
                  <th>Domain</th>
                  <th>Tenant</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>SSL</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {domains.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-data">
                      No domains found
                    </td>
                  </tr>
                ) : (
                  domains.map(domain => (
                    <tr key={domain.id} className="domain-row">
                      <td>
                        <input type="checkbox" />
                      </td>
                      <td className="domain-name">
                        <div className="domain-info">
                          <strong>{domain.full_domain || domain.domain_name}</strong>
                          {domain.is_primary && (
                            <span className="primary-badge">PRIMARY</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="tenant-info">
                          <strong>{domain.tenant?.name}</strong>
                          <div className="tenant-slug">/{domain.tenant?.slug}</div>
                        </div>
                      </td>
                      <td>
                        <span className={`type-badge type-${domain.domain_type}`}>
                          {domain.domain_type === 'subdomain' ? 'Subdomain' : 'Custom Domain'}
                        </span>
                      </td>
                      <td>
                        {getStatusBadge(domain.status)}
                      </td>
                      <td>
                        <DomainStatus domain={domain} />
                      </td>
                      <td className="date-cell">
                        {new Date(domain.created_at).toLocaleDateString()}
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          {domain.status === 'pending' && (
                            <button
                              onClick={() => handleVerifyDns(domain.id)}
                              className="btn-sm btn-primary"
                              title="Verify DNS"
                            >
                              <i className="fas fa-check"></i>
                            </button>
                          )}
                          
                          {domain.status === 'active' && !domain.ssl_enabled && (
                            <button
                              onClick={() => handleEnableSsl(domain.id)}
                              className="btn-sm btn-success"
                              title="Enable SSL"
                            >
                              <i className="fas fa-lock"></i>
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleShowDnsInstructions(domain)}
                            className="btn-sm btn-secondary"
                            title="DNS Instructions"
                          >
                            <i className="fas fa-info"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {pagination && (
          <div className="table-pagination">
            <div className="pagination-info">
              Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
              {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
              {pagination.total} domains
            </div>
            
            <div className="pagination-controls">
              <button
                onClick={() => fetchDomains(pagination.current_page - 1)}
                disabled={pagination.current_page <= 1}
                className="btn-secondary btn-sm"
              >
                <i className="fas fa-chevron-left"></i>
                Previous
              </button>
              
              <span className="page-info">
                Page {pagination.current_page} of {pagination.last_page}
              </span>
              
              <button
                onClick={() => fetchDomains(pagination.current_page + 1)}
                disabled={pagination.current_page >= pagination.last_page}
                className="btn-secondary btn-sm"
              >
                Next
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      {showConfigModal && (
        <DomainConfigModal
          onSave={handleDomainCreated}
          onClose={() => setShowConfigModal(false)}
        />
      )}

      {showDnsModal && selectedDomain && (
        <DnsInstructions
          domain={selectedDomain}
          onClose={() => setShowDnsModal(false)}
        />
      )}
    </div>
  );
};

export default DomainManagement;